import * as React from 'react';

/**
 * Report whether a single-line, ellipsis-truncated label is actually cut off.
 *
 * Attach `ref` to an element and pass the selector of the label inside it (or
 * omit the selector to measure the element itself). The measurement re-runs
 * when `text` changes and, where the browser supports it, whenever the label
 * resizes, which covers the drawer opening and closing.
 *
 * @param text     - The label text, used to re-measure when it changes
 * @param selector - CSS selector of the label inside the ref'd element
 */
export const useIsTruncated = <T extends HTMLElement>(text: string, selector?: string) => {
  const ref = React.useRef<T>(null);
  const [truncated, setTruncated] = React.useState(false);

  React.useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const label = selector ? root.querySelector<HTMLElement>(selector) ?? root : root;
    const measure = () => setTruncated(label.scrollWidth > label.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(label);
    return () => observer.disconnect();
  }, [text, selector]);

  return { ref, truncated };
};
